import { getScanPhotoScanVideoId, type ScanPhoto } from '@kaiizen/kai-scan-template-system';

/**
 * Facts about a photo that read the same wherever its detail is shown.
 *
 * These sat inside the closeout pipeline's builders until the overlay gained a third host. They
 * describe a photo, not a closeout, so they live with the overlay and each host's own builder
 * reaches for them.
 */

/** When the photo was taken, as the record holds it. */
export const getPhotoDate = (photo: ScanPhoto | undefined): number | undefined => photo?.addedAt;

/** Whoever added it, resolved against a name map the host supplies. */
export const getPhotoFieldUserName = (
  photo: ScanPhoto | undefined,
  userNameById?: Record<string, string | undefined>,
): string | undefined => photo?.addedByUserId ? userNameById?.[photo.addedByUserId] : undefined;

/**
 * A still Kai pulled out of a walkthrough video, rather than a photo someone framed.
 * Reads the canonical provenance via `getScanPhotoScanVideoId` (scanVideoId, falling back to
 * legacy orgVideoId) so stills from the VideoToScopeJob pipeline — which now stamps scanVideoId —
 * keep their "Video still" / "Video by" labels.
 */
export const isVideoFrame = (photo: ScanPhoto): boolean => Boolean(getScanPhotoScanVideoId(photo));

/** How it arrived, in the mockup's words. Anything not uploaded was taken on site. */
export const getPhotoSourceLabel = (photo: ScanPhoto): string =>
  isVideoFrame(photo)
    ? 'Video still'
    : photo.photoSource === 'upload'
    ? 'Upload from Computer'
    : 'Taken by Phone';

/** Who to credit — the videographer for a still, otherwise whoever was on site. */
export const getPhotoContributorLabel = (photo: ScanPhoto): string => isVideoFrame(photo) ? 'Video by' : 'Field user';

/**
 * The date as the caption and the info rows show it — "Feb 3, 2026".
 *
 * The app's own `formatDate` renders `05-25-26`, which the mockup does not use anywhere in this
 * overlay; the closeout builders already spell the long form out for themselves.
 */
export const formatPhotoDate = (value: number | undefined): string | undefined => {
  if (value == null) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};
